/**
 * Yol tanımları ve eşleme.
 *
 * Bu modül SAF: `window`'a, `history`'ye, React'e dokunmaz. Tarayıcıya bağlanma
 * işi `router.ts`'in; buranın tek işi "şu metin hangi sayfadır" sorusunu
 * cevaplamak. Ayrım bilinçli — yönlendirme hatalarının neredeyse tamamı
 * eşlemede olur ve eşlemeyi bir tarayıcı açmadan doğrulayabilmek gerekir.
 *
 * ## Neden router kütüphanesi yok
 *
 * Site beş statik sayfa + bir panel. `react-router-dom` bunun için ~20 kB gzip
 * getirirdi ve CI'da 300 kB'lık bir JS bütçesi zorlanıyor. Dinamik segment,
 * iç içe layout, veri yükleyici veya route guard'a ihtiyaç doğduğu gün bu
 * dosyanın yerine kütüphane konur — tüketiciler `matchRoute` ve `PATHS`
 * dışında bir şey bilmiyor.
 */

export type RouteId =
  | 'HOME'
  | 'PROGRAM_DETAIL'
  | 'SECTION_REDIRECT'
  | 'AUTH'
  | 'RESET_PASSWORD'
  | 'PANEL'
  | 'NOT_FOUND';

/**
 * Adreslenebilir SABİT yollar.
 *
 * `NOT_FOUND` bir yol değil eşleşmeme durumu; `PROGRAM_DETAIL` ise sabit
 * değil — slug taşıyor ve `programPath()` ile üretiliyor.
 */
export const PATHS = {
  HOME: '/',
  AUTH: '/giris',
  RESET_PASSWORD: '/sifre-sifirla',
  PANEL: '/panel',
} as const satisfies Record<
  Exclude<RouteId, 'NOT_FOUND' | 'PROGRAM_DETAIL' | 'SECTION_REDIRECT'>,
  string
>;

/**
 * Tanıtım sitesi TEK SAYFA: Hakkımızda, Eğitimler, Eğitmenler ve İletişim
 * ana sayfanın bölümleri. Bağlantılar `/#bölüm` biçiminde; başka bir
 * sayfadan (program detayı, giriş) tıklanınca da ana sayfaya dönüp o
 * bölüme kaydırır (`router.ts` → `scrollToHash`).
 */
export const SECTION_IDS = {
  ABOUT: 'hakkimizda',
  CLASSES: 'egitimler',
  INSTRUCTORS: 'egitmenler',
  TESTIMONIALS: 'yorumlar',
  CONTACT: 'iletisim',
} as const;

export type SectionKey = keyof typeof SECTION_IDS;

export function sectionPath(section: SectionKey): string {
  return `${PATHS.HOME}#${SECTION_IDS[section]}`;
}

/** Program detaylarının öneki — eski "Eğitimler" sayfasının adresi. */
const PROGRAMS_BASE = '/egitimler';

/**
 * Eski ayrı sayfaların adresleri → ana sayfadaki bölüm.
 *
 * Site ayrı sayfalardan tek sayfaya geçti; bu adresler paylaşılmış, arama
 * motorunda ve yer imlerinde duruyor olabilir. 404 yerine ilgili bölüme
 * yönlenirler. Galeri artık Hakkımızda bölümünün içinde.
 */
const LEGACY_SECTION_PATHS: Readonly<Record<string, string>> = {
  '/hakkinda': SECTION_IDS.ABOUT,
  '/galeri': SECTION_IDS.ABOUT,
  [PROGRAMS_BASE]: SECTION_IDS.CLASSES,
  '/egitmenler': SECTION_IDS.INSTRUCTORS,
  '/iletisim': SECTION_IDS.CONTACT,
};

/** Eski bir sayfa adresiyse yönleneceği bölümün kimliği; değilse `null`. */
export function legacySectionFor(rawPath: string): string | null {
  return LEGACY_SECTION_PATHS[normalizePath(rawPath)] ?? null;
}

/**
 * Program detay yolu: `/egitimler/rp-duo`.
 *
 * ## Neden hâlâ router kütüphanesi yok
 *
 * Bu dosyanın başındaki not "dinamik segment ihtiyacı doğduğu gün kütüphane
 * konur" diyordu. O gün geldi ama ihtiyaç TEK bir segment: program slug'ı.
 * `react-router` bunun için ~20 kB gzip getirir ve CI'da 300 kB'lık bir bütçe
 * zorlanıyor. Tek dinamik rotayı açıkça ele almak, kütüphanenin tamamını
 * getirmekten hem ucuz hem okunur. İkinci ve üçüncü dinamik rota geldiğinde
 * karar yeniden verilmeli — o zaman kütüphane doğru cevap olur.
 */
export function programPath(slug: string): string {
  return `${PROGRAMS_BASE}/${encodeURIComponent(slug)}`;
}

/** Slug yalnızca küçük harf, rakam ve tire. Şemadaki `slug` üretimiyle aynı. */
const PROGRAM_SLUG = /^[a-z0-9-]+$/;

/**
 * Program detay yolundan slug okur; o yol değilse `null`.
 *
 * Doğrulama BURADA yapılıyor: eşleşmeyen bir değer `NOT_FOUND`'a düşmeli ki
 * `/egitimler/<uydurma>` adresi 404 göstersin, boş bir detay sayfası değil.
 */
export function programSlugFromPath(rawPath: string): string | null {
  const path = normalizePath(rawPath);
  const prefix = `${PROGRAMS_BASE}/`;
  if (!path.startsWith(prefix)) return null;

  const slug = path.slice(prefix.length);
  if (slug === '' || slug.includes('/')) return null;
  return PROGRAM_SLUG.test(slug) ? slug : null;
}

/** Girişten sonra nereye dönüleceğini taşıyan sorgu parametresi. */
export const NEXT_PARAM = 'next';

const ROUTE_ENTRIES = Object.entries(PATHS) as ReadonlyArray<[RouteId, string]>;

/**
 * Kimlik gerektirmeyen sayfalar.
 *
 * Bu liste bir GÖRÜNÜRLÜK kararıdır, güvenlik sınırı değil: panel verisinin
 * tamamı `/api/v1` üzerinden gelir ve orada `requireAuth` uygulanır. Buradaki
 * bir hata en kötü ihtimalle boş bir ekran gösterir, veri sızdırmaz.
 */
const PUBLIC_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  'HOME',
  'PROGRAM_DETAIL',
  'SECTION_REDIRECT',
  'RESET_PASSWORD',
  'NOT_FOUND',
]);

/**
 * Yolu karşılaştırılabilir tek bir biçime indirger.
 *
 * `/Egitimler/?a=1#x` ile `/egitimler` aynı sayfadır. Normalleştirme olmadan
 * paylaşılan bir bağlantıdaki sondaki eğik çizgi 404 üretirdi.
 */
export function normalizePath(rawPath: string): string {
  const withoutQuery = rawPath.split('?')[0] ?? '';
  const withoutHash = withoutQuery.split('#')[0] ?? '';
  const lowered = withoutHash.toLowerCase();

  if (lowered === '' ) return PATHS.HOME;
  if (lowered === '/') return PATHS.HOME;

  const trimmed = lowered.replace(/\/+$/, '');
  return trimmed === '' ? PATHS.HOME : trimmed;
}

/**
 * Yolu bir sayfaya çevirir.
 *
 * Panelin ALT yolları da panele aittir (`/panel/rezervasyonlar` → `PANEL`):
 * panel içi gezinme hâlâ `AppTab` state'iyle yapılıyor, yani alt yollar bugün
 * üretilmiyor — ama elle yazılmış bir adres kullanıcıyı 404'e düşürmemeli.
 */
export function matchRoute(rawPath: string): RouteId {
  const path = normalizePath(rawPath);

  if (path === PATHS.PANEL || path.startsWith(`${PATHS.PANEL}/`)) return 'PANEL';

  /* Program detayı TAM eşleşmeden ÖNCE denenir. `/egitimler` sabit yolu zaten
     listede ve `/egitimler/rp-duo` ona eşleşmiyor; ama sıra tersine olsaydı
     ileride eklenecek bir "en yakın önek" kuralı sessizce yanlış sayfayı
     açardı. Geçersiz slug `null` döner ve NOT_FOUND'a düşer. */
  if (programSlugFromPath(path) !== null) return 'PROGRAM_DETAIL';

  if (legacySectionFor(path) !== null) return 'SECTION_REDIRECT';

  const matched = ROUTE_ENTRIES.find(([, value]) => value === path);
  return matched?.[0] ?? 'NOT_FOUND';
}

export function isPublicRoute(route: RouteId): boolean {
  return PUBLIC_ROUTES.has(route);
}

/**
 * Giriş ekranına, dönüş adresi taşıyarak gider.
 *
 * Dönüş adresi yalnızca `/` ile başlayan ve `//` ile başlamayan yollara izin
 * verir. İkinci koşul kritik: `//kotu.example.com` tarayıcı için protokol-relatif
 * bir MUTLAK adrestir ve doğrulanmadan bırakılırsa açık yönlendirme (open
 * redirect) açığı olurdu — kullanıcı bizim giriş ekranımızdan çıkıp başka bir
 * siteye düşerdi.
 */
export function authPathWithNext(nextPath: string): string {
  const isSafe = nextPath.startsWith('/') && !nextPath.startsWith('//');
  if (!isSafe) return PATHS.AUTH;
  return `${PATHS.AUTH}?${NEXT_PARAM}=${encodeURIComponent(nextPath)}`;
}

/** `?next=` değerini okur; güvenli değilse panele düşer. */
export function readNextPath(search: string): string {
  const raw = new URLSearchParams(search).get(NEXT_PARAM);
  if (raw === null) return PATHS.PANEL;
  if (!raw.startsWith('/') || raw.startsWith('//')) return PATHS.PANEL;
  return raw;
}

/**
 * Giriş ekranında oturumu olan kullanıcının gideceği yer.
 *
 * Şifre rotasyonu bekleyen kullanıcı `?next=`'e BAKILMAKSIZIN panele gider:
 * rotasyon ekranı yalnızca panel kabuğunda çiziliyor ve sunucu zaten diğer
 * tüm uçları 403 ile kapatıyor, yani `?next=/egitimler` gibi bir hedef ona
 * şifre sormadan açılan ama hiçbir şey yapamadığı bir sayfa olurdu.
 *
 * Bu karar önceden `App.tsx` içinde "rotasyon bekleyeni yönlendirme" diye
 * yazılmıştı ve onu rotasyon ekranına götüren BAŞKA bir yol da yoktu:
 * kullanıcı giriş yapıyor, giriş formunun önünde kalıyor ve sebebini
 * göremiyordu. Canlıda geçici şifreyle giren ilk yönetici tam bunu yaşadı.
 */
export function postLoginPath(search: string, mustChangePassword: boolean): string {
  if (mustChangePassword) return PATHS.PANEL;
  return readNextPath(search);
}
